"use client";

import React, { useState, useEffect, useRef } from "react";
import { setCookie, getCookie } from "cookies-next";
import { CookieConsentProps } from "@/cms/models";
import Link from "next/link";
import SwitchControl from "./ui/Switch";
import { usePathname } from "next/navigation";

export const cookieConsentName = "cookieAccepted";
const cookieConsentMaxAge = 365 * 24 * 60 * 60; // 1 year

type ConsentOptions = {
  necessary: boolean;
  functional: boolean;
  statistics: boolean;
  marketing: boolean;
};

function getEnabledConsentList(consentOptions: ConsentOptions): string {
  return Object.entries(consentOptions)
    .filter(([, value]) => value)
    .map(([key]) => key)
    .join(", ");
}

const CookieConsent: React.FC<CookieConsentProps> = () => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const acceptAllBtnRef = useRef<HTMLButtonElement>(null);

  const [isCustomVisible, setIsCustomVisible] = useState(false);
  const [consentOptions, setConsentOptions] = useState({
    necessary: true,
    functional: false,
    statistics: false,
    marketing: false,
  });

  const pathname = usePathname()

  useEffect(() => {
    const consent = getCookie(cookieConsentName);
    if (!consent && dialogRef.current && pathname !== "/cookies") {
      dialogRef.current.showModal();
      acceptAllBtnRef.current?.focus();
    }
  }, [pathname]);

  const handleFunctionalChange = () => {
    setConsentOptions((prev) => ({
      ...prev,
      functional: !prev.functional,
    }));
  };

  const handleStatisticsChange = () => {
    setConsentOptions((prev) => ({
      ...prev,
      statistics: !prev.statistics,
    }));
  };

  const handleMarketingChange = () => {
    setConsentOptions((prev) => ({
      ...prev,
      marketing: !prev.marketing,
    }));
  };

  const handleAcceptAll = () => {
    setCookie(
      cookieConsentName,
      getEnabledConsentList({
        necessary: true,
        functional: true,
        statistics: true,
        marketing: true,
      }),
      {
        maxAge: cookieConsentMaxAge,
      }
    );
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const handleAllowNecessary = () => {
    setCookie(
      cookieConsentName,
      getEnabledConsentList({
        necessary: true,
        functional: false,
        statistics: false,
        marketing: false,
      }),
      {
        maxAge: cookieConsentMaxAge,
      }
    );
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const handleToggleCustomVisible = () => {
    setIsCustomVisible(!isCustomVisible);
  };

  const handleAllowCustom = () => {
    setCookie(cookieConsentName, getEnabledConsentList(consentOptions), {
      maxAge: cookieConsentMaxAge,
    });
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const handleClose = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="w-full max-w-[460px] mx-4 p-8 backdrop:bg-black/80 lg:max-w-[760px] md:fixed md:bottom-2 md:left-1/3 md:-translate-x-1/2 rounded-lg lg:rounded-b-none"
      onClose={handleClose}
    >
      <div>
        {isCustomVisible == false && (
          <div>
            <h2 className="text-3xl font-bold mb-4">
              Hjemmesiden benytter cookies
            </h2>
            <p className="homepage-text-sm text-gray-800 font-semibold mb-6">
              Denne hjemmeside anvender egne cookies og cookies fra
              tredjeparter. Cookies benyttes til statistik og til at få
              hjemmesiden til at fungere så optimalt som muligt for dig som
              bruger.{" "}
              <Link onClick={() => dialogRef.current?.close()} href="/cookies" aria-label="Cookies" className="text-gray-600 underline">
                Læs mere om cookies her
              </Link>
            </p>
            <div className="flex flex-col lg:flex-row  gap-2">
              <button
                onClick={handleAllowNecessary}
                className="flex-1 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-base font-semibold"
              >
                Tillad nødvendige
              </button>
              <button
                onClick={handleToggleCustomVisible}
                className="flex-1 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-base font-semibold"
              >
                Tilpas
              </button>
              <button
                ref={acceptAllBtnRef}
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-4 bg-[#3C3C3B] text-white hover:bg-[#2C2C2B] text-base font-semibold"
              >
                Accepter alle
              </button>
            </div>
          </div>
        )}

        {isCustomVisible && (
          <div>
            <h2 className="text-3xl font-bold mb-4">
              Dine cookie indstillinger
            </h2>
            <div className="space-y-3 mb-6">
              <div className="flex flex-col gap-4">
                {/* Necessary */}
                <div className="flex gap-4">
                  <div>
                    <SwitchControl
                      checked={true}
                      disabled={true}
                      onChange={() => {}}
                    />
                  </div>
                  <div className="homepage-text-sm">
                    <h3 className="font-semibold mb-2">Nødvendige</h3>
                    <p>
                      Nødvendige cookies er påkrævet for at gøre websitet
                      brugbart eksempelvis ifht. navigation.
                    </p>
                  </div>
                </div>

                {/* Functional */}

                <div className="flex gap-4">
                  <div>
                    <SwitchControl
                      checked={consentOptions.functional}
                      onChange={handleFunctionalChange}
                    />
                  </div>
                  <div className="homepage-text-sm">
                    <h3 className="font-semibold mb-2">Funktionelle</h3>
                    <p>
                      For at kunne vise den version af websitet, som passer
                      bedst til dine præferencer, indsamler vi oplysninger om
                      dine valg. Et eksempel kunne være hvilket sprog eller
                      region, som du foretrækker.
                    </p>
                  </div>
                </div>

                {/* Statistics */}

                <div className="flex gap-4">
                  <div>
                    <SwitchControl
                      checked={consentOptions.statistics}
                      onChange={handleStatisticsChange}
                    />
                  </div>
                  <div className="homepage-text-sm">
                    <h3 className="font-semibold mb-2">Statistiske</h3>
                    <p>
                      Vi indsamler oplysninger om din adfærd på websitet for at
                      kunne optimere design og brugervenlighed.
                    </p>
                  </div>
                </div>

                {/* Marketing */}

                <div className="flex gap-4">
                  <div>
                    <SwitchControl
                      checked={consentOptions.marketing}
                      onChange={handleMarketingChange}
                    />
                  </div>
                  <div className="homepage-text-sm">
                    <h3 className="font-semibold mb-2">Marketing</h3>
                    <p>
                      For at kunne vise målrettede annoncer, som er relevant for
                      dig, indsamler vi oplysninger om dine interesser samt
                      produkter eller ydelser, som du evt. måtte klikke på.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row  gap-2">
              <button
                onClick={handleAllowCustom}
                className="flex-1 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-base font-semibold"
              >
                Tillad valgte
              </button>
              <button
                onClick={handleToggleCustomVisible}
                className="flex-1 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-base font-semibold"
              >
                Tilbage
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-4 bg-[#3C3C3B] text-white hover:bg-[#2C2C2B] text-base font-semibold"
              >
                Accepter alle
              </button>
            </div>
          </div>
        )}
      </div>
    </dialog>
  );
};

export default CookieConsent;
